/**
 * Authenticated app shell.
 *
 * Collapsible sidebar navigation (shadcn `Sidebar`, `collapsible="icon"`) beside
 * the routed content (`SidebarInset` + `<Outlet/>`). The sidebar collapses to an
 * icon rail on desktop (toggle via the trigger or Ctrl/Cmd+B, persisted in a
 * cookie) and becomes a slide-over sheet on mobile. The top bar carries the
 * trigger, theme switcher and user menu.
 */
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { Bell, BookMarked, LogOut, MessageSquare, Settings, ShieldCheck, Users } from 'lucide-react'
import { type ComponentType, useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { Monogram } from '@/components/brand/logo'
import { ModeToggle } from '@/components/mode-toggle'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { useUserDoc } from '@/hooks/useUserDoc'
import { useAuth } from '@/lib/auth'
import { syncMicrosoftPhoto } from '@/lib/callables'

interface NavItem {
  to: '/' | '/reminders' | '/clients' | '/memories' | '/settings' | '/admin'
  label: string
  icon: ComponentType<{ className?: string }>
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Conversas', icon: MessageSquare },
  { to: '/reminders', label: 'Lembretes', icon: Bell },
  { to: '/clients', label: 'Clientes', icon: Users },
  { to: '/memories', label: 'Memórias', icon: BookMarked },
  { to: '/settings', label: 'Configurações', icon: Settings },
]

/** Item de navegação exclusivo de administradores (exibido apenas com o claim `admin`). */
const ADMIN_NAV_ITEM: NavItem = { to: '/admin', label: 'Admin', icon: ShieldCheck }

/** Read the persisted collapse state so a reload keeps the sidebar as the user left it. */
function readSidebarCookie(): boolean {
  if (typeof document === 'undefined') return true
  const match = document.cookie.match(/(?:^|;\s*)sidebar_state=(true|false)/)
  return match ? match[1] === 'true' : true
}

function initialsFrom(name?: string | null, email?: string | null): string {
  const source = name?.trim() || email?.trim() || ''
  if (!source) return '?'
  const parts = source.split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + second).toUpperCase() || source[0]?.toUpperCase() || '?'
}

function AppSidebar() {
  const { pathname } = useLocation()
  const { claims } = useAuth()

  const navItems = claims?.admin === true ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <Link
          to="/"
          activeOptions={{ exact: true }}
          className="flex items-center gap-2.5 rounded-md px-1 py-1"
          aria-label="PG Advogados — Agente Executivo, ir para o início"
        >
          <Monogram size={32} className="shrink-0" />
          <div className="flex flex-col leading-tight group-data-[collapsible=icon]:hidden">
            <span className="font-display text-sm font-semibold tracking-tight">PG Advogados</span>
            <span className="text-muted-foreground text-xs">Agente executivo</span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map(({ to, label, icon: Icon }) => {
              // '/' also stays active while inside a conversation thread.
              const isActive =
                to === '/'
                  ? pathname === '/' || pathname.startsWith('/conversations')
                  : pathname.startsWith(to)
              return (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    tooltip={label}
                    // Orange brand rail on the active item (kept invisible at rest).
                    className='before:bg-brand relative before:absolute before:top-1.5 before:bottom-1.5 before:left-0 before:w-0.5 before:rounded-full before:opacity-0 before:transition-opacity before:content-[""] data-[active=true]:before:opacity-100'
                  >
                    <Link to={to} activeOptions={{ exact: to === '/' }}>
                      <Icon className="size-4 shrink-0" />
                      <span>{label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}

function UserMenu() {
  const { user, signOut } = useAuth()
  const { userDoc } = useUserDoc()
  const navigate = useNavigate()

  // Prefer the Microsoft Graph photo; fall back to the Firebase Auth photo, then initials.
  const photoUrl = userDoc?.microsoft?.photoUrl ?? user?.photoURL ?? undefined

  // Once connected with no stored photo yet, pull it from Graph (best-effort).
  const photoSyncTried = useRef(false)
  const connected = userDoc?.microsoft?.connected === true
  const hasPhoto = Boolean(userDoc?.microsoft?.photoUrl)
  useEffect(() => {
    if (photoSyncTried.current || !connected || hasPhoto) return
    photoSyncTried.current = true
    syncMicrosoftPhoto().catch(() => {
      // Non-fatal: leave the initials fallback and allow a retry next session.
      photoSyncTried.current = false
    })
  }, [connected, hasPhoto])

  async function handleSignOut() {
    try {
      await signOut()
      await navigate({ to: '/login' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Tente novamente.'
      toast.error('Não foi possível sair', { description: message })
    }
  }

  const displayName = user?.displayName ?? user?.email ?? 'Usuário'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-auto gap-2 px-2 py-1.5" aria-label="Menu do usuário">
          <Avatar className="size-7">
            {photoUrl ? <AvatarImage src={photoUrl} alt="" /> : null}
            <AvatarFallback>{initialsFrom(user?.displayName, user?.email)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-[10rem] truncate text-sm font-medium sm:inline">
            {displayName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium">{displayName}</span>
          {user?.email ? (
            <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="size-4" />
            Configurações
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            void handleSignOut()
          }}
        >
          <LogOut className="size-4" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AppShell() {
  return (
    <SidebarProvider defaultOpen={readSidebarCookie()}>
      <AppSidebar />
      <SidebarInset className="h-svh overflow-hidden">
        <header className="bg-background/80 border-border sticky top-0 z-10 flex h-14 items-center gap-2 border-b px-3 backdrop-blur md:px-6">
          <SidebarTrigger className="-ml-1" aria-label="Alternar barra lateral" />
          <div className="flex-1" />
          <ModeToggle />
          <UserMenu />
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}

export default AppShell
